import supabaseClient from "../lib/supabaseClient";
import { FRONTEND_URL } from "../env-config";
import { apiService } from "./apiService";
import { Profile } from "../types/user";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { getBaseUrl } from "../utils/subdomainDetection"; // Import dynamic base URL helper

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface RegisterPayload extends AuthCredentials {
  fullName: string;
  redirectUrl?: string; // Add optional redirect URL
}

export type User = SupabaseUser;

export const authService = {
  /**
   * Sign in (alias for login)
   */
  login: async ({ email, password }: AuthCredentials): Promise<User> => {
    const { data, error } = await supabaseClient.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    if (!data.session || !data.session.user) {
      throw new Error("Login failed: no session user");
    }
    return data.session.user;
  },

  /**
   * Register a new user
   */
  register: async ({
    email,
    password,
    fullName,
    redirectUrl, // Accept optional redirect URL
  }: RegisterPayload): Promise<User> => {
    // Use provided redirectUrl or dynamically determine it
    const emailRedirectTo = redirectUrl || `${getBaseUrl()}/auth/v1/callback`;
    
    // Only log registration details in development to avoid PII leakage
    if (process.env.NODE_ENV === 'development') {
      console.log("📧 authService.register - Registration attempt:", {
        hasEmail: !!email,
        hasPassword: !!password,
        hasFullName: !!fullName,
        hasRedirectUrl: !!redirectUrl,
        redirectUrlProvided: !!redirectUrl,
      });
    }
    
    const { data, error } = await supabaseClient.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo, // Use dynamic URL
      },
    });
    
    // Only log response details in development
    if (process.env.NODE_ENV === 'development') {
      console.log("📧 Supabase signUp response:", {
        success: !!data.user,
        hasError: !!error,
        errorMessage: error?.message,
      });
    }
    
    if (error) throw error;
    if (!data.user) {
      throw new Error("Registration failed: no user returned");
    }
    return data.user;
  },

  /**
   * Original sign in with password
   */
  signIn: (email: string, password: string) =>
    supabaseClient.auth.signInWithPassword({ email, password }),

  /**
   * Original sign up (alias to register)
   */
  signUp: (email: string, password: string, fullName: string, redirectUrl?: string) => {
    const emailRedirectTo = redirectUrl || `${getBaseUrl()}/auth/v1/callback`;
    return supabaseClient.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo, // Use dynamic URL
      },
    });
  },

  /**
   * Sign in via Google OAuth
   */
  signInWithGoogle: () =>
    supabaseClient.auth.signInWithOAuth({ provider: "google" }),

  /**
   * Sign out the current user (alias to logout)
   */
  signOut: () => supabaseClient.auth.signOut(),

  /**
   * Alias for supabase sign out
   */
  logout: async (): Promise<void> => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) throw error;
  },

  /**
   * Get current session
   */
  getSession: () => supabaseClient.auth.getSession(),

  /**
   * Get the current authenticated user
   */
  getCurrentUser: async () => {
    const {
      data: { user },
      error,
    } = await supabaseClient.auth.getUser();
    if (error) throw error;
    return user;
  },

  /**
   * Fetch the user's profile record
   */
  getUserProfile: async (): Promise<Profile | null> => {
    const user = await authService.getCurrentUser();
    if (!user) return null;
    const { data, error } = await supabaseClient
      .from("users")
      .select("id, full_name, email, created_at, updated_at")
      .eq("id", user.id)
      .single();
    if (error) throw error;

    // Transform to match Profile interface
    return {
      id: data.id,
      full_name: data.full_name || "",
      onboarded: true, // Assume onboarded if they have a profile
      updated_at: data.updated_at,
    } as Profile;
  },

  /**
   * Check if the user has completed onboarding
   */
  isUserOnboarded: async (): Promise<boolean> => {
    const profile = await authService.getUserProfile();
    return Boolean(profile?.onboarded);
  },

  /**
   * Check auth state (alias for getCurrentUser)
   */
  checkAuth: async (): Promise<User | null> => {
    const user = await authService.getCurrentUser();
    if (!user) {
      throw new Error("Session expired or no user logged in");
    }
    return user;
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange: (
    cb: (
      event: import("@supabase/supabase-js").AuthChangeEvent,
      session: import("@supabase/supabase-js").Session | null
    ) => void
  ) => {
    const { data: subscriptionData } =
      supabaseClient.auth.onAuthStateChange(cb);
    supabaseClient.auth
      .getSession()
      .then(({ data: { session } }) => {
        const initialEvent: import("@supabase/supabase-js").AuthChangeEvent =
          session ? "SIGNED_IN" : "SIGNED_OUT";
        cb(initialEvent, session);
      })
      .catch((err) => {
        console.error("Error fetching initial session:", err);
        try {
          cb("SIGNED_OUT", null);
        } catch (e) {
          console.error("Error invoking auth state change callback:", e);
        }
      });
    return {
      unsubscribe: () => subscriptionData.subscription.unsubscribe(),
    };
  },

  /**
   * Update the user's metadata (e.g., mark onboarding complete)
   */
  updateUserMetadata: async (metadata: {
    [key: string]: any;
  }): Promise<void> => {
    const {
      data: { user },
    } = await supabaseClient.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Update the users table instead of non-existent profiles table
    const { error } = await supabaseClient
      .from("users")
      .update({
        full_name: metadata.full_name,
        onboarding: metadata.onboarding
          ? { onboarding_complete: true }
          : undefined,
        updated_at: new Date().toISOString(),
      })
      .eq("id", user.id);

    if (error) {
      throw error;
    }
  },

  /**
   * Check user onboarding status from the backend
   */
  checkOnboardingStatus: async (): Promise<{
    exists: boolean;
    onboardingComplete: boolean;
    hasFullName: boolean;
  }> => {
    const result = await apiService.checkOnboardingStatus();
    if (!result) throw new Error("Failed to check onboarding status");
    return result;
  },
};

export default authService;
